/* eslint-disable react-hooks/exhaustive-deps */

import {
    ReactNode,
    createContext,
    FC,
    useReducer,
    useEffect,
    Dispatch
} from 'react';

import styled from '@emotion/styled';
import isEqual from 'lodash/isEqual';

import {
    DfpTargeting,
    AdvertContextType,
    ZPG_DFP_NETWORK,
    DFP_AD_UNIT,
    AdvertContextAction,
    ACTION_TYPE,
    Slot,
    AD_UNIT_SECTION
} from './types';
import usePrevious from './usePreviousHook';
import { REPORT_AD_URL } from './constants';
import DfpManager from './dfpManager';
import { logInfo } from '@zoopla/logger';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';
import {
    getOptimizelyFeatureFlag,
    useSelectForcedFeatures
} from '@zoopla/optimizely';

type defaultContext = {
    state: AdvertContextType;
    dispatch: Dispatch<AdvertContextAction>;
    mountSlot: (slot: Slot) => void;
    unmountSlot: (slot: Pick<Slot, 'id'>) => void;
};

const ReportButton = styled.button`
    visibility: hidden;
    display: none;
`;

const defaultState: AdvertContextType = {
    dfpInitialised: false,
    adBridgInitialised: false,
    slots: {},
    targeting: {},
    dfpNetworkId: ZPG_DFP_NETWORK.ID,
    adUnitCode: DFP_AD_UNIT.DEFAULT,
    showPlaceHolderContent: false,
    debugMode: false,
    reportMode: false
};

const defaultValue: defaultContext = {
    state: { ...defaultState },
    dispatch: () => ({}),
    mountSlot: () => void 0,
    unmountSlot: () => void 0
};

export const advertReducer = (
    state: AdvertContextType,
    action: AdvertContextAction
): AdvertContextType => {
    const slotsCopy = { ...state.slots };
    switch (action.type) {
        case ACTION_TYPE.SET_DFP_MANAGER: {
            action.payload.refresh();
            action.payload.defineSlots(slotsCopy);
            const keys = Object.keys(slotsCopy);
            keys.forEach((key) => (slotsCopy[key].isDefined = true));
            return {
                ...state,
                dfpManager: action.payload,
                slots: slotsCopy
            };
        }
        case ACTION_TYPE.MOUNT_SLOT: {
            const slotToMount = { ...action.payload };
            return {
                ...state,
                slots: { ...state.slots, [slotToMount.id]: slotToMount }
            };
        }
        case ACTION_TYPE.UNMOUNT_SLOT:
            delete slotsCopy[action.payload.id];
            return {
                ...state,
                slots: slotsCopy
            };
        case ACTION_TYPE.SET_TARGETING:
            return {
                ...state,
                targeting: { ...action.payload }
            };
        case ACTION_TYPE.SET_INITIALISED:
            return {
                ...state,
                dfpInitialised: true
            };
        case ACTION_TYPE.REPORT_MODE:
            return {
                ...state,
                reportMode: true
            };
        case ACTION_TYPE.REPORT_SLOT:
            return {
                ...state,
                reportMode: false,
                reportAdSlotId: action.payload.id,
                reportAdSlotPosition: action.payload.position
            };
        case ACTION_TYPE.SET_SHUTDOWN:
            return {
                ...state,
                adBridgInitialised: false
            };
        case ACTION_TYPE.SET_ADBRIDG_INITIALISED:
            return {
                ...state,
                adBridgInitialised: true
            };
        default:
            return state;
    }
};

export const AdvertContext = createContext(defaultValue);
AdvertContext.displayName = 'AdvertContext';

export type AdvertProviderProps = {
    children?: ReactNode;
    value: {
        adUnitCode?: DFP_AD_UNIT.DEFAULT;
        dfpNetworkId?: ZPG_DFP_NETWORK.ID;
        targeting: DfpTargeting;
        showPlaceHolderContent?: boolean;
        initialState?: AdvertContextType;
        debugMode?: boolean;
        pageNumber?: number;
        adBridgPage?: AD_UNIT_SECTION;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatchOverride?: any;
};

const AdvertProvider: FC<AdvertProviderProps & WithOptimizelyProps> = ({
    children,
    value: {
        targeting,
        dfpNetworkId = ZPG_DFP_NETWORK.ID,
        adUnitCode = DFP_AD_UNIT.DEFAULT,
        showPlaceHolderContent = false,
        initialState = defaultState,
        debugMode = false,
        pageNumber,
        adBridgPage
    },
    dispatchOverride,
    optimizely
}) => {
    const [state, dispatch] = useReducer(advertReducer, {
        ...initialState,
        dfpNetworkId,
        adUnitCode,
        targeting,
        showPlaceHolderContent,
        debugMode
    });

    const {
        dfpInitialised,
        reportAdSlotId,
        reportAdSlotPosition,
        dfpManager,
        adBridgInitialised
    } = state;

    const prevTargeting = usePrevious(targeting);
    const prevPageNumber = usePrevious(pageNumber);

    const mountSlot = (slot: Slot) => {
        if (dfpManager) {
            dfpManager.defineSlot(slot);
            slot.isDefined = true;
        }
        dispatch({ type: ACTION_TYPE.MOUNT_SLOT, payload: slot });
    };

    const unmountSlot = (slot: Pick<Slot, 'id'>) => {
        dfpManager?.destroySlot(slot.id);
        dispatch({ type: ACTION_TYPE.UNMOUNT_SLOT, payload: slot });
    };

    const featureFlagOverrides = useSelectForcedFeatures();

    const isAdBridgEnabled = getOptimizelyFeatureFlag(
        'use_adbridg',
        featureFlagOverrides,
        optimizely
    );

    // load DfpManager once on client side mount
    useEffect(() => {
        const dfpManagerInstance = new DfpManager(
            targeting,
            debugMode,
            isAdBridgEnabled,
            adBridgPage
        );
        dispatch({
            type: ACTION_TYPE.SET_DFP_MANAGER,
            payload: dfpManagerInstance
        });
        dfpManagerInstance.log('mounted');
    }, [isAdBridgEnabled]);

    useEffect(() => {
        if (dfpManager && !dfpInitialised) {
            dfpManager.init(() => {
                dispatch({ type: ACTION_TYPE.SET_INITIALISED });
            });
        }
        if (!adBridgInitialised && dfpInitialised) {
            dfpManager?.initAdbridg();
            dispatch({ type: ACTION_TYPE.SET_ADBRIDG_INITIALISED });
        }
    }, [dfpManager, dfpInitialised, adBridgInitialised]);

    const updatedPageNumber =
        prevPageNumber && !isEqual(prevPageNumber, pageNumber);
    const updatedTargeting =
        prevTargeting && !isEqual(prevTargeting, targeting);

    useEffect(() => {
        if (dfpManager && updatedTargeting) {
            dispatch({ type: ACTION_TYPE.SET_TARGETING, payload: targeting });
            dfpManager.log('updateTargeting');
            dfpManager.configureTargeting(targeting);
            dfpManager.refreshTargeting();
        }
        if (
            dfpManager &&
            dfpInitialised &&
            (updatedPageNumber || updatedTargeting)
        ) {
            dfpManager.log('refreshcheck');
            dfpManager.shutdown();
            dispatch({ type: ACTION_TYPE.SET_SHUTDOWN });
            dfpManager.refresh();
        }
    }, [pageNumber, targeting, prevTargeting, dfpManager, dfpInitialised]);

    useEffect(() => {
        if (reportAdSlotId && dfpManager) {
            const matchingAdSlot = dfpManager.adDataMap?.[reportAdSlotId]?.[0];

            const postData = async () => {
                try {
                    await fetch(REPORT_AD_URL, {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            slotId: reportAdSlotId,
                            creativeId: matchingAdSlot?.creativeId,
                            sourceAgnosticCreativeId:
                                matchingAdSlot?.sourceAgnosticCreativeId,
                            dfpNetworkId,
                            adUnitCode,
                            position: reportAdSlotPosition,
                            siteUrl: window.location.href
                        })
                    });
                    alert('Creative has been reported');
                } catch (e) {
                    logInfo('Error sending slack alert', e);
                }
            };

            postData();
        }
    }, [reportAdSlotId, dfpManager]);

    const reportAds = () => {
        dispatch({ type: ACTION_TYPE.REPORT_MODE });
    };

    return (
        <AdvertContext.Provider
            value={{
                state,
                dispatch: dispatchOverride || dispatch,
                mountSlot,
                unmountSlot
            }}
        >
            <ReportButton
                data-testid="trigger_report_ads"
                id="trigger_report_ads"
                onClick={reportAds}
            >
                Enable reporting
            </ReportButton>
            {children}
        </AdvertContext.Provider>
    );
};

export const AdvertProviderWithOptimizely = withOptimizely(AdvertProvider);
